import { useEffect, useRef } from 'react';

const SLIDE_SELECTOR = '.hub-mobile-panel-carousel-slide';
const EDGE_THRESHOLD = 40;
const JUMP_DELAY_MS = 80;

/**
 * モバイルパネルカルーセルを無限ループにする。
 * コンテナの子は [最後のクローン, ...実スライド, 最初のクローン] の順であること。
 * @param {React.RefObject} containerRef - スクロールコンテナの ref
 * @param {number} realCount - 実スライド数（クローン除く）
 * @param {boolean} enabled - 有効にするか（モバイル時のみ true にすること）
 */
export function useInfiniteCarouselScroll(containerRef, realCount, enabled) {
  const isJumpingRef = useRef(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!enabled || !containerRef?.current || realCount < 1) return;

    const el = containerRef.current;
    const slides = el.querySelectorAll(SLIDE_SELECTOR);
    if (slides.length < realCount + 2) return;

    const firstReal = slides[1];
    const lastReal = slides[slides.length - 2];

    const jumpWithoutAnimation = (targetScrollLeft) => {
      const prev = el.style.scrollBehavior;
      el.style.scrollBehavior = 'auto';
      el.scrollLeft = targetScrollLeft;
      el.style.scrollBehavior = prev || '';
    };
    const scrollToRealFirst = () => {
      isJumpingRef.current = true;
      jumpWithoutAnimation(firstReal.offsetLeft - (el.clientWidth - firstReal.offsetWidth) / 2);
      requestAnimationFrame(() => { isJumpingRef.current = false; });
    };
    const scrollToRealLast = () => {
      isJumpingRef.current = true;
      jumpWithoutAnimation(lastReal.offsetLeft - (el.clientWidth - lastReal.offsetWidth) / 2);
      requestAnimationFrame(() => { isJumpingRef.current = false; });
    };

    const init = () => {
      jumpWithoutAnimation(firstReal.offsetLeft - (el.clientWidth - firstReal.offsetWidth) / 2);
    };
    init();

    const onScroll = () => {
      if (isJumpingRef.current) return;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      const { scrollLeft, scrollWidth, clientWidth } = el;
      const maxScroll = scrollWidth - clientWidth;
      if (maxScroll <= 0) return;

      if (scrollLeft <= EDGE_THRESHOLD) {
        timeoutRef.current = setTimeout(scrollToRealLast, JUMP_DELAY_MS);
      } else if (scrollLeft >= maxScroll - EDGE_THRESHOLD) {
        timeoutRef.current = setTimeout(scrollToRealFirst, JUMP_DELAY_MS);
      }
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [containerRef, realCount, enabled]);
}
